import {
  BgColor,
  bgColorClassName,
  borderClassName,
  BorderColor,
  FgColor,
  fgColorClassName,
} from "./colors";
import {
  alignItemsMapping,
  Breakpoint,
  columnGapMapping,
  containerClassName,
  fontWeightMapping,
  gapMapping,
  justifyContentMapping,
  justifyItemsMapping,
  justifySelfMapping,
  marginBottomMapping,
  marginLeftMapping,
  marginRightMapping,
  marginTopMapping,
  marginXMapping,
  marginYMapping,
  MaxWidth,
  maxWidthClassName,
  paddingBottomMapping,
  paddingLeftMapping,
  paddingMapping,
  paddingRightMapping,
  paddingTopMapping,
  paddingXMapping,
  paddingYMapping,
  rowGapMapping,
  textSizeMapping,
} from "./tailwindMappings";
import { HTMLProps, ReactNode } from "react";
import { classNames } from "../utils/classNames";
import { TMessage } from "../i18n/tt";

export type CommonProps = {
  testId?: string;
  testType?: string;
  className?: string;
  fullscreen?: boolean;
  fullheight?: boolean;
  bg?: BgColor | boolean;
  fg?: FgColor | boolean;
  border?: BorderColor | boolean;
  borderBottom?: BorderColor | boolean;
  borderLeft?: BorderColor | boolean;
  borderRight?: BorderColor | boolean;
  borderTop?: BorderColor | boolean;
  padding?: "xs" | "sm" | "md" | "lg" | boolean;
  paddingX?: "xs" | "sm" | "md" | "lg" | boolean;
  paddingY?: "xs" | "sm" | "md" | "lg" | boolean;
  paddingTop?: "xs" | "sm" | "md" | "lg" | boolean;
  paddingBottom?: "xs" | "sm" | "md" | "lg" | boolean;
  paddingLeft?: "xs" | "sm" | "md" | "lg" | boolean;
  paddingRight?: "xs" | "sm" | "md" | "lg" | boolean;
  marginX?: "auto" | "sm" | "md" | "lg" | boolean;
  marginY?: "auto" | "sm" | "md" | "lg" | boolean;
  marginLeft?: "auto" | "sm" | "md" | "lg" | boolean;
  marginRight?: "auto" | "sm" | "md" | "lg" | boolean;
  marginTop?: "auto" | "sm" | "md" | "lg" | boolean;
  marginBottom?: "auto" | "sm" | "md" | "lg" | boolean;
  gap?: "xs" | "sm" | "md" | "lg" | "xl" | boolean | 0;
  rowGap?: "xs" | "sm" | "md" | "lg" | "xl" | boolean | 0;
  columnGap?: "xs" | "sm" | "md" | "lg" | "xl" | boolean | 0;
  overflow?: "auto" | "hidden";
  overflowY?: "auto" | "hidden";
  overflowX?: "auto" | "hidden";
  maxWidth?: MaxWidth;
  dropShadow?: boolean | "lg" | "xl" | "top";
  /**
   * Alignment / distribution of space along the main axis. In a horizontal
   * box "start" means to lay out the components from left to right (the default),
   * "end" means to right align the items, and "center" means to center the items.
   */
  justifyContent?: "start" | "end" | "center" | "space-between" | "space-around" | "space-evenly";
  justifySelf?: "start" | "end" | "center" | "stretch";
  /**
   * Alignment on the cross axis. In a horizontal box, "start" means top
   * "end" means bottom, and "stretch" means fill from top to bottom.
   * Defaults to "center".
   */
  alignItems?: "start" | "end" | "center" | "stretch" | "baseline";
  /**
   * Alignment on the inline axis in a block or grid layout. Ignored in flex layout.
   */
  justifyItems?: "start" | "end" | "center" | "stretch";
  rounded?: boolean | "lg" | "full";
  roundedTop?: boolean | "lg" | "full";
  roundedBottom?: boolean | "lg" | "full";
  whitespace?: "nowrap";
  textAlign?: "left" | "center" | "right";
  textSize?: keyof typeof textSizeMapping;
  fontWeight?: keyof typeof fontWeightMapping;
  italic?: boolean;
  container?: boolean | Breakpoint;
  grow?: boolean | "golden";
  tooltip?: string | TMessage;
  hoverBg?: boolean;
};

export type BoxProps = CommonProps & {
  children?: ReactNode;
  display?: "flex" | "block" | "inline-flex";
  vertical?: boolean;
  flex?: "auto" | "none";
  wrap?: boolean;
  sticky?: boolean | "top" | "bottom";
  onPinnedChange?: (pinned: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  htmlProps?: Omit<HTMLProps<HTMLDivElement>, "className"> & Record<string, any>;
};

export function commonPropsToClassName({
  className,
  fullscreen,
  fullheight,
  bg,
  fg,
  border,
  borderBottom,
  borderLeft,
  borderRight,
  borderTop,
  padding,
  paddingX,
  paddingY,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  marginX,
  marginY,
  marginLeft,
  marginRight,
  marginTop,
  marginBottom,
  gap,
  rowGap,
  columnGap,
  overflow,
  overflowY,
  overflowX,
  maxWidth,
  dropShadow,
  justifyContent,
  justifyItems,
  justifySelf,
  alignItems,
  rounded,
  roundedTop,
  roundedBottom,
  whitespace,
  textAlign,
  textSize,
  fontWeight,
  italic,
  container,
  grow,
  hoverBg,
}: CommonProps): string {
  paddingX = paddingX ?? (container ? true : undefined);
  return classNames(
    className,
    fullscreen && "tw-absolute tw-inset-0 h-full w-full",
    fullheight && "tw-h-full",
    bgColorClassName(bg),
    fgColorClassName(fg),
    borderClassName("tw-border", border),
    borderClassName("tw-border-b", borderBottom),
    borderClassName("tw-border-l", borderLeft),
    borderClassName("tw-border-r", borderRight),
    borderClassName("tw-border-t", borderTop),
    padding && paddingMapping[typeof padding === "boolean" ? "md" : padding],
    paddingX && paddingXMapping[typeof paddingX === "boolean" ? "md" : paddingX],
    paddingY && paddingYMapping[typeof paddingY === "boolean" ? "md" : paddingY],
    paddingTop && paddingTopMapping[typeof paddingTop === "boolean" ? "md" : paddingTop],
    paddingBottom &&
      paddingBottomMapping[typeof paddingBottom === "boolean" ? "md" : paddingBottom],
    paddingLeft && paddingLeftMapping[typeof paddingLeft === "boolean" ? "md" : paddingLeft],
    paddingRight && paddingRightMapping[typeof paddingRight === "boolean" ? "md" : paddingRight],
    marginX && marginXMapping[marginX === true ? "md" : marginX],
    marginY && marginYMapping[marginY === true ? "md" : marginY],
    marginLeft && marginLeftMapping[marginLeft === true ? "md" : marginLeft],
    marginRight && marginRightMapping[marginRight === true ? "md" : marginRight],
    marginTop && marginTopMapping[marginTop === true ? "md" : marginTop],
    marginBottom && marginBottomMapping[marginBottom === true ? "md" : marginBottom],
    gap === true || gap === undefined ? gapMapping["sm"] : gap && gapMapping[gap],
    rowGap === true ? rowGapMapping["sm"] : rowGap && rowGapMapping[rowGap],
    columnGap === true ? columnGapMapping["sm"] : columnGap && columnGapMapping[columnGap],
    overflow === "auto" && "tw-overflow-auto",
    overflow === "hidden" && "tw-overflow-hidden",
    overflowY === "auto" && "tw-overflow-y-auto",
    overflowY === "hidden" && "tw-overflow-y-hidden",
    overflowX === "auto" && "tw-overflow-x-auto",
    overflowX === "hidden" && "tw-overflow-x-hidden",
    maxWidth && maxWidthClassName(maxWidth),
    dropShadow === true && "tw-shadow-md",
    dropShadow === "lg" && "tw-shadow-lg",
    dropShadow === "top" && "tw-shadow-top-md",
    dropShadow === "xl" && "tw-drop-shadow-2xl",
    justifyContent && justifyContentMapping[justifyContent],
    justifySelf && justifySelfMapping[justifySelf],
    justifyItems && justifyItemsMapping[justifyItems],
    alignItems && alignItemsMapping[alignItems],
    rounded === "full"
      ? "tw-rounded-full"
      : rounded === "lg"
        ? "tw-rounded-lg"
        : rounded
          ? "tw-rounded"
          : undefined,
    roundedTop === "full"
      ? "tw-rounded-t-full"
      : roundedTop === "lg"
        ? "tw-rounded-t-lg"
        : roundedTop
          ? "tw-rounded-t"
          : undefined,
    roundedBottom === "full"
      ? "tw-rounded-b-full"
      : roundedBottom === "lg"
        ? "tw-rounded-b-lg"
        : roundedBottom
          ? "tw-rounded-b"
          : undefined,
    whitespace && "tw-whitespace-nowrap",
    textAlign === "left"
      ? "tw-text-left"
      : textAlign === "center"
        ? "tw-text-center"
        : textAlign === "right"
          ? "tw-text-right"
          : undefined,
    textSize && textSizeMapping[textSize],
    fontWeight && fontWeightMapping[fontWeight],
    italic === true ? "tw-italic" : italic === false ? "tw-not-italic" : undefined,
    container && containerClassName(container),
    grow !== undefined &&
      (grow === true ? "tw-grow" : grow === false ? "tw-grow-0" : "tw-grow-[0.625]"),
    hoverBg && `hover:bg-primary-light`,
  );
}

export const boxClassNames = (props: BoxProps): string =>
  classNames(
    commonPropsToClassName(props),
    !props.display || props.display === "flex"
      ? "tw-flex"
      : props.display === "inline-flex"
        ? "tw-inline-flex"
        : "tw-block",
    props.vertical && "tw-flex-col",
    props.flex === "auto" && "tw-flex-auto",
    props.flex === "none" && "tw-flex-none",
    props.wrap && "tw-flex-wrap",
    props.sticky === "bottom"
      ? "tw-sticky -tw-bottom-px"
      : props.sticky
        ? "tw-sticky tw-top-0"
        : undefined,
  );
